import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import {
  assignCustomersToUpload,
  getUploadStatus,
  uploadFileToWatchList,
} from 'services/clients/watchList';
import {
  BlueCargoProperty,
  UploadedFileColumn,
} from 'pages/TrackAndTrace/components/add-containers/add-form/stages/customer-assignment/column-mapping';
import { Assignment, CustomerAssignment } from 'pages/Shipments/components/customer-assignment';
import { CloudUpload, Warning } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShippingContainer from 'assets/images/shipping-container.png';
import { useSegment } from 'common/hooks/segment';
import { Message } from './Message';
import { UploadForm } from './UploadForm';

interface Props {
  onTrackAndTrace: () => void;
}

interface StartStage {
  stage: 'start';
  acceptedFiles: File[];
  inputContainerNumbers: string[];
  /** For uploading manually entered container numbers */
  uploading: boolean;
}

interface UploadStage {
  stage: 'uploading';
  uploadId: string | null;
  columns: UploadedFileColumn[];
  fileName: string | null;
  /** Whether the job status was success */
  success: boolean;
}

interface DuplicateStage {
  stage: 'duplicate';
}

interface AssignmentStage {
  stage: 'assignment';
  assignment: Assignment;
  uploadId: string | null;
  columns: UploadedFileColumn[];
  fileName: string;
  /** Whether the job status was success */
  success: boolean;
}

interface CompletionStage {
  stage: 'complete';
}

interface ErrorStage {
  stage: 'error';
  message?: string;
  originStage: 'uploading' | 'assignment';
}

type Stage =
  | StartStage
  | UploadStage
  | DuplicateStage
  | AssignmentStage
  | CompletionStage
  | ErrorStage;

export const UploadContainerForm: React.FC<Props> = (props) => {
  const { onTrackAndTrace } = props;

  const queryClient = useQueryClient();

  const { track } = useSegment();

  /**
   * This component sends the user through a flow, so we keep track of the state or each step
   * here. This avoids us having extra confusing state at all times that isn't relevent for a
   * given stage
   */
  const initialState = React.useMemo<StartStage>(
    () => ({
      stage: 'start',
      acceptedFiles: [],
      inputContainerNumbers: [],
      uploading: false,
    }),
    []
  );
  const [state, setState] = React.useState<Stage>(initialState);

  const [showModal, setShowModal] = React.useState(false);

  /** The upload ID is derived from our staged state */
  const uploadId = React.useMemo(
    () => (state.stage === 'uploading' || state.stage === 'assignment' ? state.uploadId : null),
    [state]
  );

  /** Upload a list of container number files */
  const uploadFileContainerNumbers = React.useCallback(
    async (files: File[]) => {
      if (
        state.stage !== 'start' ||
        (state.acceptedFiles.length === 0 && state.inputContainerNumbers.length === 0)
      ) {
        return;
      }

      const file = files[0];
      setState({
        stage: 'uploading',
        uploadId: null,
        columns: [],
        fileName: file.name,
        success: false,
      });
      try {
        const response = await uploadFileToWatchList(file, null, 'bill_of_ladings');
        if (response.status === 'duplicate') {
          setState({ stage: 'duplicate' });
          return;
        }

        const columns: UploadedFileColumn[] = [];
        const {
          firstRows,
          numberOfColumnsFound,
          customerColumn,
          hasHeader,
          containerNumberColumn,
        } = response;

        if (firstRows.length >= 1) {
          for (let i = 0; i < numberOfColumnsFound; i += 1) {
            const name = hasHeader ? firstRows[0][i] : '-';
            const preview = firstRows[hasHeader ? 1 : 0][i];
            let mapping: BlueCargoProperty | null = null;

            if (containerNumberColumn !== null && containerNumberColumn === i) {
              mapping = 'container';
            } else if (customerColumn !== null && customerColumn === i) {
              mapping = 'customer';
            }

            const row: UploadedFileColumn = {
              id: i.toString(),
              name,
              preview,
              mapping,
            };
            columns.push(row);
          }
        }

        track(
          'Added shipment',
          { type: 'bulk', response, success: true },
          { includeCompany: true, includeUser: true }
        );

        setState({
          stage: 'uploading',
          uploadId: response.containerTransactionWatchImportId,
          columns,
          fileName: file.name,
          success: false,
        });
      } catch (e) {
        track(
          'Added shipment',
          { type: 'bulk', success: false },
          { includeCompany: true, includeUser: true }
        );
        setState({ stage: 'error', originStage: 'uploading' });
      }
    },
    [state, setState, track]
  );

  /** Assigns a customer tag or the column to the last upload id */
  const assignCustomer = React.useCallback(async () => {
    if (!uploadId || state.stage !== 'assignment') {
      return;
    }

    const selectedCustomerId =
      (state.assignment.kind === 'dropdown' && state.assignment.customer?.id) || null;
    let selectedColumnIndex: number | null = null;
    if (state.assignment.kind === 'column') {
      const index = state.assignment.columns.findIndex((c) => c.mapping === 'customer');
      if (index >= 0) {
        selectedColumnIndex = index;
      }
    }

    if (selectedCustomerId == null && selectedColumnIndex === null) {
      setState({ stage: 'complete' });
      return;
    }

    try {
      await assignCustomersToUpload({
        containerTransactionWatchImportId: uploadId,
        selectedCustomerId,
        selectedColumnIndex,
      });
      queryClient.invalidateQueries(['customers']);
      setState({ stage: 'complete' });
    } catch (error) {
      setState({ stage: 'error', originStage: 'assignment' });
    }
  }, [state, uploadId, queryClient]);

  const handleSetFiles = React.useCallback(
    (files: File[]) => {
      setState((s) => ({
        ...s,
        acceptedFiles: files,
      }));
    },
    [setState]
  );

  const shouldGetUploadStatus = !!(
    uploadId &&
    (state.stage === 'uploading' || state.stage === 'assignment') &&
    !state.success
  );

  useQuery(['getUploadStatus', uploadId], () => getUploadStatus(uploadId), {
    refetchInterval: shouldGetUploadStatus ? 2000 : false,
    enabled: shouldGetUploadStatus,
    onSuccess: (data) => {
      // Keep in mind that we only pull when there is an uploadId id and success is fals,
      // which only occurs in the uploading and assignment stages.
      setState((s) => {
        switch (data.status) {
          case 'duplicate':
            return { stage: 'duplicate' };
          case 'error':
            return { stage: 'error', originStage: 'uploading', message: data.message };
          case 'in_progress':
          case 'success':
          case 'pending':
          case 'retrying': {
            // If we're in uploading, we want to proceed to customer assignment
            if (s.stage === 'uploading') {
              return {
                stage: 'assignment',
                columns: s.columns,
                assignment: { kind: 'none' },
                uploadId: s.uploadId || '',
                fileName: s.fileName || '',
                success: data.status === 'success',
              };
            }

            // If we're in assignment, we want to mark success if that's the case so we stop polling
            if (s.stage === 'assignment' && data.status === 'success') {
              return { ...s, success: true };
            }

            return s;
          }
          default:
            return s;
        }
      });
    },
    onError: () => {
      setState({ stage: 'error', originStage: 'uploading' });
    },
  });

  const onAssignment = React.useCallback(
    (assignment: Assignment) => setState((s) => ({ ...s, assignment })),
    [setState]
  );

  let modalContent: React.ReactNode = null;
  const uploadSpaceIsDisabled = state.stage !== 'start';
  const buttonIsDisabled = state.stage !== 'start' || state.acceptedFiles.length === 0;

  switch (state.stage) {
    case 'start':
      break;
    case 'uploading':
      modalContent = (
        <Message
          title="Your Bill of Ladings are uploading..."
          description="This might take a couple of minutes, hang tight!"
          hero={<img src={ShippingContainer} alt="Shipping container" className="w-52 m-8" />}
        />
      );
      break;
    case 'assignment':
      modalContent = (
        <CustomerAssignment
          columns={state.columns}
          onAssignment={onAssignment}
          fileName={state.fileName}
        />
      );
      break;
    case 'complete':
      modalContent = (
        <Message
          title="Your Bill of Ladings were successfully uploaded!"
          description="The containers will be added to your watchlist."
          hero={
            <CheckCircleIcon
              className="text-green-600"
              style={{
                fontSize: '54px',
              }}
            />
          }
        />
      );
      break;
    case 'duplicate':
      modalContent = (
        <Message
          title="Duplicate file"
          description="This file has already been uploaded, please modify your file and its name to upload it again."
          hero={
            <Warning
              className="text-orange-900"
              style={{
                fontSize: '54px',
              }}
            />
          }
        />
      );
      break;
    case 'error':
      modalContent = (
        <Message
          title="There was an error processing your Bill of Ladings. Please try again."
          description="If the error persists, please contact support@bluecargo.io"
          hero={
            <Warning
              className="text-orange-900"
              style={{
                fontSize: '54px',
              }}
            />
          }
        />
      );
      break;
    default:
      break;
  }

  return (
    <>
      <Dialog open={showModal} fullWidth maxWidth="lg">
        <DialogContent dividers>
          <div>{modalContent}</div>
        </DialogContent>
        {state.stage !== 'uploading' && (
          <DialogActions className="my-2 mx-3">
            {state.stage === 'complete' || state.stage === 'error' ? (
              <Button
                onClick={() => {
                  setShowModal(false);
                  setState(initialState);
                }}
                variant="outlined"
                size="large"
                className="text-gray-500 border-gray-500 w-24"
              >
                Close
              </Button>
            ) : (
              <Button
                onClick={async () => {
                  if (state.stage === 'assignment') await assignCustomer();
                  onTrackAndTrace();
                  setState({ stage: 'complete' });
                }}
                variant="outlined"
                size="large"
                className="text-gray-500 border-gray-500 w-24"
              >
                Continue
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="max-h-72 overflow-auto py-4">
          <UploadForm onFileSelected={handleSetFiles} disabled={uploadSpaceIsDisabled} />
        </div>
        <div className="flex justify-end">
          <LoadingButton
            variant="contained"
            color="primary"
            disabled={buttonIsDisabled}
            className="items-center"
            startIcon={<CloudUpload className="w-4" />}
            onClick={async () => {
              if (state.stage !== 'start') return;
              setShowModal(true);
              await uploadFileContainerNumbers(state.acceptedFiles);
            }}
            size="large"
            type="submit"
            loading={state.stage === 'start' && state.uploading}
          >
            <h4>Upload file</h4>
          </LoadingButton>
        </div>
      </form>
    </>
  );
};
UploadContainerForm.displayName = 'Shipments.AddContainers.UploadContainerForm';
